import Client from '@/repositories/clients/AuthenticatedClient';
import { ref } from 'vue';

/**
 * Pinned-panels API (`/pinnedPanels`) backed by a module-level reactive cache.
 *
 * Convention: these functions UNWRAP responses and maintain the local
 * `pinnedPanels` ref — they do not return the AxiosResponse. Callers read the
 * returned array/boolean or the shared ref via `usePinnedPanels()`.
 */
const PINNED_PANELS_PATH = '/pinnedPanels';

const pinnedPanels = ref([]);
let loadPromise = null;

function panelKey(dashboardId, panelId) {
  return `${dashboardId}::${panelId}`;
}

/** Load + cache pinned panels (deduped in-flight; falls back to []). Unwrapped. @returns {Promise<object[]>} pinned items */
export async function loadPinnedPanels() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const response = await Client.get(PINNED_PANELS_PATH);
      pinnedPanels.value = response?.data?.items || [];
      return pinnedPanels.value;
    } catch (error) {
      pinnedPanels.value = [];
      return [];
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
}

/** Pin (PUT) or unpin (DELETE) a panel and update the local cache. @param {string} dashboardId @param {string} panelId @param {boolean} pinned @returns {Promise<void>} */
export async function setPanelPinned(dashboardId, panelId, pinned) {
  if (pinned) {
    const response = await Client.put(PINNED_PANELS_PATH, {
      dashboard_id: dashboardId,
      panel_id: panelId,
    });
    const item = response?.data;
    if (item) {
      const without = pinnedPanels.value.filter(
        (p) => panelKey(p.dashboard_id, p.panel_id) !== panelKey(dashboardId, panelId)
      );
      pinnedPanels.value = [item, ...without];
    }
  } else {
    await Client.delete(
      `${PINNED_PANELS_PATH}/${encodeURIComponent(dashboardId)}/${encodeURIComponent(panelId)}`
    );
    pinnedPanels.value = pinnedPanels.value.filter(
      (p) => panelKey(p.dashboard_id, p.panel_id) !== panelKey(dashboardId, panelId)
    );
  }
}

/** Whether a panel is currently pinned (reads the local cache). @param {string} dashboardId @param {string} panelId @returns {boolean} */
export function isPanelPinned(dashboardId, panelId) {
  const key = panelKey(dashboardId, panelId);
  return pinnedPanels.value.some((p) => panelKey(p.dashboard_id, p.panel_id) === key);
}

/** Composable exposing the shared ref + actions. @returns {{ pinnedPanels: import('vue').Ref<object[]>, loadPinnedPanels: Function, setPanelPinned: Function, isPanelPinned: Function }} */
export function usePinnedPanels() {
  return { pinnedPanels, loadPinnedPanels, setPanelPinned, isPanelPinned };
}

export default {
  loadPinnedPanels,
  setPanelPinned,
  isPanelPinned,
  usePinnedPanels,
  pinnedPanels,
};
